import type { VoucherLinePayload, VoucherState } from '../../lib/types';

/**
 * A browser-local recovery copy for a new voucher only. The company id is both the object key
 * and stored on the record, so a switched company can never accidentally restore another
 * company's Journal/Contra lines.
 */
export interface LocalVoucherDraft {
  companyId: string;
  voucherTypeId: string;
  branchId: string;
  financialYearId: string;
  voucherDate: string;
  state: VoucherState;
  narration: string;
  reference: string;
  lines: Array<VoucherLinePayload & { key: string }>;
  savedAt: string;
}

const DATABASE = 'accounting-platform-voucher-drafts';
const STORE = 'new-voucher-by-company';

function supported(): boolean {
  return typeof window !== 'undefined' && 'indexedDB' in window;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!supported()) {
      reject(new Error('This browser does not provide local draft storage.'));
      return;
    }
    const request = window.indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE, { keyPath: 'companyId' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open local draft storage.'));
  });
}

async function transact<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      let result: T;
      request.onsuccess = () => { result = request.result; };
      request.onerror = () => reject(request.error ?? new Error('Local draft storage operation failed.'));
      transaction.oncomplete = () => resolve(result!);
      transaction.onerror = () => reject(transaction.error ?? new Error('Local draft storage transaction failed.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Local draft storage transaction was aborted.'));
    });
  } finally {
    database.close();
  }
}

export async function loadVoucherDraft(companyId: string): Promise<LocalVoucherDraft | null> {
  const draft = await transact<LocalVoucherDraft | undefined>('readonly', (store) => store.get(companyId));
  if (!draft || draft.companyId !== companyId || !Array.isArray(draft.lines)) return null;
  return draft;
}

export async function saveVoucherDraft(draft: LocalVoucherDraft): Promise<void> {
  await transact<IDBValidKey>('readwrite', (store) => store.put(draft));
}

export async function clearVoucherDraft(companyId: string): Promise<void> {
  await transact<undefined>('readwrite', (store) => store.delete(companyId));
}
